// Panell d'Horari d'un Empleat: es mostra en substitució del formulari
// principal de Crear/Modificar/Consultar Empleat (botó "..." al costat del
// camp "Horari"). Reutilitza el mateix HorariEditor que l'Horari d'Empresa
// per no duplicar l'editor de Períodes/Rangs/Trams.

import React, { useState, useEffect, useCallback } from "react";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import HorariEditor from "./HorariEditor";
import "./horariempresa.css";

const AVUI = new Date().toISOString().split("T")[0];

function HorariEmpleat({ idEmpleat, idEmpresa, nomEmpleat, readOnly = false, onTornar }) {
  const [carregant, setCarregant] = useState(true);
  const [error, setError] = useState("");
  const [assignacioVigent, setAssignacioVigent] = useState(null);
  const [horari, setHorari] = useState(null);
  const [historial, setHistorial] = useState([]);

  const [accio, setAccio] = useState(null); // 'agafar' | 'crear' | 'canviar' | 'recuperar' | null
  const [mostrarEditor, setMostrarEditor] = useState(false);
  const [dataEfectiva, setDataEfectiva] = useState(AVUI);
  const [idHorariRecuperar, setIdHorariRecuperar] = useState(null);
  const [enviant, setEnviant] = useState(false);
  const [errorAccio, setErrorAccio] = useState("");

  const carregar = useCallback(async () => {
    if (!idEmpleat || !idEmpresa) {
      // Sense idEmpleat/idEmpresa no podem demanar res: evitem quedar-nos
      // encallats mostrant "Carregant..." per sempre.
      setCarregant(false);
      setError("Falten dades (Empleat o Empresa) per carregar l'Horari.");
      return;
    }
    setCarregant(true);
    setError("");
    try {
      const res = await fetchAutenticat(`${API_URL}/EmpleatHorariActual.php?idEmpleat=${idEmpleat}&idEmpresa=${idEmpresa}`);
      const data = await res.json();
      if (data.success) {
        setAssignacioVigent(data.assignacioVigent);
        setHorari(data.horari);
        setHistorial(data.historial || []);
      } else {
        setError(data.message || "No s'ha pogut carregar l'Horari de l'Empleat.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setCarregant(false);
    }
  }, [idEmpleat, idEmpresa]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const tancarAccio = () => {
    setAccio(null);
    setErrorAccio("");
    setIdHorariRecuperar(null);
    setDataEfectiva(AVUI);
  };

  TeclaEscEnter(() => {
    if (accio) tancarAccio();
    else if (mostrarEditor) setMostrarEditor(false);
    else onTornar?.();
  }, null);

  const executarAccio = async () => {
    if (!dataEfectiva) {
      setErrorAccio("Indica la data d'inici.");
      return;
    }
    if (accio === "recuperar" && !idHorariRecuperar) {
      setErrorAccio("Selecciona quin Horari vols recuperar.");
      return;
    }
    setEnviant(true);
    setErrorAccio("");
    try {
      let url;
      let body;
      if (accio === "agafar") {
        url = `${API_URL}/EmpleatAgafarHorariEmpresa.php`;
        body = { idEmpleat, idEmpresa, DataInici: dataEfectiva };
      } else if (accio === "crear") {
        url = `${API_URL}/EmpleatCrearHorariPropi.php`;
        body = { idEmpleat, idEmpresa, DataInici: dataEfectiva };
      } else {
        url = `${API_URL}/EmpleatRecuperarHorari.php`;
        body = { idEmpleat, idEmpresa, idHoraris: idHorariRecuperar, DataInici: dataEfectiva };
      }
      const res = await fetchAutenticat(url, { method: "POST", body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        tancarAccio();
        setMostrarEditor(true);
        await carregar();
      } else {
        setErrorAccio(data.message || "No s'ha pogut completar l'acció.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorAccio("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  const renderFormAccio = () => {
    if (!accio) return null;
    const titols = {
      agafar: "Agafar l'Horari d'Empresa com a punt de partida",
      crear: "Crear un Horari propi (buit) per a l'Empleat",
      recuperar: "Recuperar un Horari anterior de l'historial",
    };
    return (
      <div className="Dashboard-form horari-empresa-formulari">
        <div className="horari-empresa-bloc-titol">{titols[accio]}</div>

        {accio === "recuperar" && (
          <div className="Dashboard-form-row">
            <label>
              Horari a recuperar:
              <select value={idHorariRecuperar ?? ""} onChange={(e) => setIdHorariRecuperar(e.target.value ? parseInt(e.target.value, 10) : null)}>
                <option value="">Selecciona un Horari de l'historial</option>
                {historial.map((h) => (
                  <option key={h.idEmpleatsHoraris} value={h.Horaris_idHoraris}>
                    {h.NomHorari} (vigent del {formatDate(h.DataInici)} al {formatDate(h.DataFinal)})
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <div className="Dashboard-form-row">
          <label>
            Data d'inici efectiva:
            <input type="date" value={dataEfectiva} onChange={(e) => setDataEfectiva(e.target.value)} />
          </label>
        </div>

        {assignacioVigent && (
          <p className="info-inline">
            L'assignació vigent ({assignacioVigent.NomHorari}, des del {formatDate(assignacioVigent.DataInici)}) es tancarà el dia
            abans de la nova data d'inici.
          </p>
        )}

        {errorAccio && <p className="horari-empresa-error">{errorAccio}</p>}

        <div className="Dashboard-button-group">
          <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={executarAccio} disabled={enviant}>
            Confirmar
          </button>
          <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={tancarAccio} disabled={enviant}>
            Cancel·lar
          </button>
        </div>
      </div>
    );
  };

  if (carregant) return <p>Carregant l'Horari de l'Empleat...</p>;

  return (
    <div className="Dashboard-form-section horari-empresa">
      <div className="Dashboard-form-row">
        <strong>Horari de {nomEmpleat || "l'Empleat"}</strong>
      </div>

      {error && <p className="horari-empresa-error">{error}</p>}

      {!assignacioVigent && !accio && (
        <>
          <p>Aquest Empleat encara no té cap Horari assignat.</p>
          {!readOnly && (
            <div className="Dashboard-button-group">
              <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={() => setAccio("agafar")}>
                Agafar horari d'empresa
              </button>
              <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={() => setAccio("crear")}>
                Crear horari nou
              </button>
            </div>
          )}
        </>
      )}

      {assignacioVigent && !accio && (
        <>
          <div className="info-inline">
            <strong>Horari vigent:</strong> {assignacioVigent.NomHorari} (des del {formatDate(assignacioVigent.DataInici)})
          </div>

          {!readOnly && (
            <div className="Dashboard-button-group">
              <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={() => setMostrarEditor((v) => !v)}>
                {mostrarEditor ? "Amagar detall" : "Consultar / modificar horari"}
              </button>
              <button type="button" className="Dashboard-btn" onClick={() => setAccio("agafar")}>
                Canviar per horari d'empresa
              </button>
              <button type="button" className="Dashboard-btn" onClick={() => setAccio("crear")}>
                Canviar per horari nou
              </button>
              {historial.length > 0 && (
                <button type="button" className="Dashboard-btn" onClick={() => setAccio("recuperar")}>
                  Recuperar horari anterior
                </button>
              )}
            </div>
          )}

          {(readOnly || mostrarEditor) && horari && (
            <HorariEditor
              idEmpresa={idEmpresa}
              horari={horari}
              onHorariActualitzat={(nouHorari) => setHorari(nouHorari)}
              onRecarregar={carregar}
              readOnly={readOnly}
            />
          )}
        </>
      )}

      {!readOnly && renderFormAccio()}

      {historial.length > 0 && !accio && (
        <details className="horari-empresa-historial">
          <summary>Historial d'Horaris ({historial.length})</summary>
          <ul>
            {historial.map((h) => (
              <li key={h.idEmpleatsHoraris}>
                {h.NomHorari}: del {formatDate(h.DataInici)} al {formatDate(h.DataFinal)}
              </li>
            ))}
          </ul>
        </details>
      )}

      {onTornar && (
        <div className="Dashboard-button-group">
          <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={onTornar}>
            Tornar
          </button>
        </div>
      )}
    </div>
  );
}

export default HorariEmpleat;
